import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '../api/auth-context';
import { AppShell } from './AppShell';
import { AuthCallback } from './AuthCallback';
import { Landing } from './Landing';
import { Licenses } from './Licenses';
import { Login } from './Login';
import { NodePage } from './NodePage';
import { RepertoireHome } from './RepertoireHome';
import { RepertoireList } from './RepertoireList';
import { ProfilePage } from './ProfilePage';
import { ReviewPage } from './ReviewPage';
import { GameReviewPage } from './GameReviewPage';
import { PublicFrame } from './SiteFooter';

/**
 * Authed area. When unauthenticated, any protected route renders the public
 * Landing instead of firing API calls; /login hosts the branded sign-in card.
 * A 401 clears the token (via the client's `onUnauthorized` hook), which flips
 * `isAuthenticated` and lands the user back on the landing.
 */
function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();
  if (!auth.isAuthenticated) return <Landing />;
  return <>{children}</>;
}

/** A public page: inside the app shell when signed in, on its own (with the footer) when not. */
function ShellIfSignedIn(): JSX.Element {
  const auth = useAuth();
  return auth.isAuthenticated ? <AppShell /> : <PublicFrame />;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Public: GPL notices must be reachable without an account (§1.3, R-17). */}
      <Route element={<ShellIfSignedIn />}>
        <Route path="/licenses" element={<Licenses />} />
      </Route>
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<RepertoireList />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/games" element={<GameReviewPage />} />
        <Route path="/repertoires/:repertoireId" element={<RepertoireHome />} />
        <Route path="/repertoires/:repertoireId/review" element={<ReviewPage />} />
        <Route
          path="/repertoires/:repertoireId/nodes/:zobristHash/:positionId"
          element={<NodePage />}
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
